import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

import { Analytics } from '@/modules/reports/interfaces/reportData'
import { getHotelReport } from '@/modules/reports/services/hotelReportApi'

interface Params {
	companyId?: number
	filters: Filters
}

interface Filters {
	dates?: string[] | null
	creationDates?: string[] | null
	status?: string
	groupBy?: string
	travelerId?: number
	hotelId?: number
	createdAt?: string[] | null
}

const getCompanyReportHotelAnalytics = async (
	params: Params
): Promise<Analytics> => {
	const { data } = await getHotelReport<Analytics>(
		params.companyId!,
		'analytics',
		{
			params: {
				checkOutDates: params.filters.dates,
				createdDate: params.filters.creationDates,
				status: params.filters.status,
				groupBy: params.filters.groupBy,
				travelerId: params.filters.travelerId,
				hotelId: params.filters.hotelId,
				createdAt: params.filters.createdAt,
			},
		}
	)

	return data
}

export default function useCompanyReportHotelAnalytics(
	params: ComputedRef<Params>
) {
	const queryKey = computed(() => [
		'company-report-hotels-analytics',
		params.value.companyId,
		params.value.filters.dates,
		params.value.filters.creationDates,
		params.value.filters.status,
		params.value.filters.groupBy,
		params.value.filters.travelerId,
		params.value.filters.hotelId,
		params.value.filters.createdAt,
	])

	const enabled = computed(() => !!params.value.companyId)

	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey,
		queryFn: () => getCompanyReportHotelAnalytics(params.value),
		enabled,
		staleTime: 0,
		cacheTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
	})

	const analytics = computed(() => data.value)

	return {
		analytics,
		isLoading: computed(() => enabled.value && isLoading.value),
		isFetching,
		error,
		refetch,
	}
}
